import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, StyleProps } from "@hex-insights/core";
import { FieldIf, Form, FormEditButtonPair, FormState, SubmitButton } from "@hex-insights/forms";
import { DiscountCreateMutation, DiscountDetailQuery, DiscountUpdateMutation } from "../../../Utilities";
import { DiscountFormState, DiscountFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<DiscountCreateMutation["createDiscount"]>,
	"formState" | "create"
> & {
	formStateArgs?: DiscountFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Discount model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<DiscountFormValues.Create>;
	create: CreateFunction<DiscountFormValues.Create, T>;
	onSuccess: (formState: FormState<DiscountFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<DiscountFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Discount model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "discount.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<DiscountFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.Name formState={formState} />
			<Field.Description formState={formState} />
			<Field.IsPercentageAmount formState={formState} />
			<FieldIf formState={formState} name="flatAmount" condition={!formState.formValues.isPercentageAmount}>
				<Field.FlatAmount formState={formState} />
			</FieldIf>
			<FieldIf formState={formState} name="percentageAmount" condition={formState.formValues.isPercentageAmount}>
				<Field.PercentageAmount formState={formState} />
			</FieldIf>
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<DiscountUpdateMutation["updateDiscount"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<DiscountFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the Discount model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, discount: props.discount });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	discount: DiscountDetailQuery["discount"];
	formState: FormState<DiscountFormValues.Detail>;
	update: UpdateFunction<DiscountFormValues.Detail, T>;
	onSuccess: (formState: FormState<DiscountFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<DiscountFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the Discount model using the given form state.
 */
export function Detail<T>({ formState, discount, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `discount.detail.${discount.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} discount={discount} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<DiscountFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the Discount model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, discount: props.discount });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	discount: DiscountDetailQuery["discount"];
	formState: FormState<DiscountFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the Discount model using the given form state.
 */
export function ReadOnly({ formState, discount, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} discount={discount} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	discount: DiscountDetailQuery["discount"];
	formState: FormState<DiscountFormValues.Detail>;
};

function DetailFields({ formState }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.Name formState={formState} />
			<Field.Description formState={formState} />
			<Field.IsActive formState={formState} />
			<Field.IsPercentageAmount formState={formState} />
			<FieldIf formState={formState} name="flatAmount" condition={!formState.formValues.isPercentageAmount}>
				<Field.FlatAmount formState={formState} />
			</FieldIf>
			<FieldIf formState={formState} name="percentageAmount" condition={formState.formValues.isPercentageAmount}>
				<Field.PercentageAmount formState={formState} />
			</FieldIf>
		</React.Fragment>
	);
}
